// ============================================================
// useChatSignals (v8.363.5) - a line for you that you are not looking at.
//
// A private vox addressed to me SUMMONS the log curtain (the mirror of
// its YIELD: opened without touching the saved layout, released by the
// next toggle) and a toast names the sender; an @mention toasts. Lines
// older than this mount, my own lines and removed lines say nothing -
// a reload is not a burst of news. `chatToasts` is a viewer preference.
// ============================================================
import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../engine/chat';
import { conversationOf } from '../engine/chat';

interface Params {
  messages: ChatMessage[];
  me: string | null;
  /** The newest line's ts when this client mounted - nothing before it is news. */
  mountedAt?: number;
  toasts: boolean;
  summon: () => void;
  pushToast: (message: string, opts?: { type?: 'info' | 'error' | 'success'; duration?: number }) => void;
}

const PREVIEW_CHARS = 80;

/** What a line says to me: a private vox summons, a private vox or a mention toasts. */
interface Signal {
  isDm: boolean;
  toast: string;
}

/** The toast's words: who, and the start of what they said (a roll shows its dice). */
function toastText(m: ChatMessage, isDm: boolean): string {
  const who = m.speakAs?.name || m.fromName || 'Someone';
  const preview =
    m.kind === 'roll' ? `🎲 ${m.roll?.expr ?? m.text} = ${m.roll?.total ?? '?'}` : m.text.slice(0, PREVIEW_CHARS);
  return isDm ? `Private vox from ${who}: ${preview}` : `${who} mentioned you: ${preview}`;
}

/** The signal a new line gives me, or null: my own, removed and pre-mount lines say nothing. */
function signalOf(m: ChatMessage, me: string, since: number): Signal | null {
  if (m.fromUid === me || m.kind === 'removed' || m.ts < since) return null;
  const isDm = conversationOf(m, me).startsWith('dm:');
  if (!isDm && !m.mentions.includes(me)) return null;
  return { isDm, toast: toastText(m, isDm) };
}

/** The lines not seen before, in order - marking them seen. */
function takeUnseen(messages: ChatMessage[], seen: Set<string>): ChatMessage[] {
  const fresh: ChatMessage[] = [];
  for (const m of messages) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    fresh.push(m);
  }
  return fresh;
}

/** The signals among new lines, in order. */
function signalsIn(fresh: ChatMessage[], me: string, since: number): Signal[] {
  return fresh.map((m) => signalOf(m, me, since)).filter((sig): sig is Signal => sig !== null);
}

/** A private vox summons the curtain; either kind toasts, when toasts are on. */
function raise(signal: Signal, toasts: boolean, summon: () => void, pushToast: Params['pushToast']) {
  if (signal.isDm) summon();
  if (toasts) pushToast(signal.toast, { type: 'info', duration: 6000 });
}

export default function useChatSignals({ messages, me, mountedAt, toasts, summon, pushToast }: Params): void {
  const seen = useRef<Set<string>>(new Set());
  const since = useRef<number>(mountedAt ?? Date.now());
  const primed = useRef(false);
  // DECLARED FIRST: on mount this must run before the news effect primes,
  // or it would un-prime it and turn the next delivery into a burst.
  useEffect(() => {
    seen.current = new Set();
    primed.current = false;
    since.current = mountedAt ?? Date.now();
  }, [me, mountedAt]);
  useEffect(() => {
    if (!me) return;
    const fresh = takeUnseen(messages, seen.current);
    // The first delivery is history, not news.
    const history = !primed.current;
    primed.current = true;
    if (history) return;
    for (const signal of signalsIn(fresh, me, since.current)) raise(signal, toasts, summon, pushToast);
  }, [messages, me, toasts, summon, pushToast]);
}
